/** RAG styling helpers for project/portfolio health metrics. */

import { ProjectDashboardDto, ProjectDeadlineHealthDto } from '../pm-reports/pm-reports.models';

export type VelocityTrendValue = 0 | 1 | 2 | 'Flat' | 'Improving' | 'Declining';

export interface NormalizedHealthMetrics {
  healthLabel?: string;
  healthScore?: number;
  riskScore?: number;
  overduePercent?: number;
  blockedPercent?: number;
  completionPercent?: number;
  velocityTrend?: number | string;
}

export function normalizeProjectDashboard(raw: ProjectDashboardDto | Record<string, unknown>): ProjectDashboardDto {
  const data = raw as Record<string, unknown>;
  const nested = data['health'] as ProjectDeadlineHealthDto | undefined;
  const metrics = extractHealthMetrics(data, nested);
  return {
    ...(raw as ProjectDashboardDto),
    ...metrics,
    health: nested,
    openIssues: (data['openIssues'] as number) ?? nested?.pending,
    completedIssues: (data['completedIssues'] as number) ?? nested?.completed,
    blockedIssues: (data['blockedIssues'] as number) ?? nested?.blocked,
    overdueIssues: (data['overdueIssues'] as number) ?? nested?.overdue,
    completionPercent: metrics.completionPercent ?? (data['completionPercent'] as number),
    upcomingDeadlines: (data['upcomingDeadlines'] as ProjectDashboardDto['upcomingDeadlines'])
      ?? nested?.upcomingDeadlines
  };
}

export function extractHealthMetrics(
  data?: Record<string, unknown> | ProjectDeadlineHealthDto | null,
  nested?: ProjectDeadlineHealthDto | null
): NormalizedHealthMetrics {
  const top = (data || {}) as Record<string, unknown>;
  const h = nested || (top as unknown as ProjectDeadlineHealthDto);
  return {
    healthLabel: (top['healthLabel'] as string) ?? h?.healthLabel,
    healthScore: (top['healthScore'] as number) ?? h?.healthScore,
    riskScore: (top['riskScore'] as number) ?? h?.riskScore,
    overduePercent: (top['overduePercent'] as number) ?? h?.overduePercent,
    blockedPercent: (top['blockedPercent'] as number) ?? h?.blockedPercent,
    completionPercent: (top['completionPercent'] as number) ?? h?.completionPercent,
    velocityTrend: (top['velocityTrend'] as number | string) ?? h?.velocityTrend
  };
}

export function healthRagClass(label?: string | null, score?: number | null): string {
  const key = (label || '').toLowerCase().replace(/\s+/g, '');
  if (key.includes('critical')) return 'rag-critical';
  if (key.includes('atrisk') || key.includes('at-risk')) return 'rag-at-risk';
  if (score != null && !Number.isNaN(score)) {
    if (score >= 70) return 'rag-healthy';
    if (score >= 40) return 'rag-at-risk';
    return 'rag-critical';
  }
  if (key.includes('healthy')) return 'rag-healthy';
  return 'rag-neutral';
}

export function velocityTrendLabel(trend?: number | string | null): string {
  if (trend == null || trend === '') return 'Flat';
  if (typeof trend === 'string') {
    const t = trend.toLowerCase();
    if (t.includes('improv')) return 'Improving';
    if (t.includes('declin')) return 'Declining';
    return 'Flat';
  }
  if (trend === 1) return 'Improving';
  if (trend === 2) return 'Declining';
  return 'Flat';
}

export function velocityTrendClass(trend?: number | string | null): string {
  const label = velocityTrendLabel(trend);
  if (label === 'Improving') return 'trend-improving';
  if (label === 'Declining') return 'trend-declining';
  return 'trend-flat';
}

export function formatPercent(value?: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Math.round(value)}%`;
}
